import { isRef, nextTick } from "vue";
import {
  type Composer,
  type I18n,
  type I18nMode,
  type VueI18n,
} from "vue-i18n";

import { i18nInstance, type Locale, SUPPORTED_LOCALES } from "@/utils/i18n.ts";

/**
 * Loads the given locale messages into the given i18n instance.
 *
 * @param i18n - The i18n instance
 * @param locale - The locale to set
 */
async function loadLocaleMessages(i18n: I18n, locale: Locale) {
  const messages = await loadTranslationFile(locale);

  i18n.global.setLocaleMessage(locale, messages.default);

  return nextTick();
}

// Export the global t function for use in utilities
export const t = i18nInstance.global.t.bind(i18nInstance.global);

export async function loadTranslationFile(locale: Locale) {
  return await import(`./locales/${locale}.json`);
}

export function setHtmlLocale(locale: string) {
  const htmlElement = document.querySelector("html");
  if (htmlElement) {
    htmlElement.setAttribute("lang", locale);
  }
}

/**
 * Loads the given locale messages into the given i18n instance and sets the current locale.
 *
 * @param i18n - The i18n instance
 * @param locale - The locale to set
 */
export async function setLocale(i18n: I18n, locale: string) {
  let internalLocale: Locale = locale as Locale;
  if (!SUPPORTED_LOCALES.includes(internalLocale)) {
    internalLocale = "en";
  }

  await loadLocaleMessages(i18n, internalLocale as Locale);

  if (isComposer(i18n.global, i18n.mode)) {
    i18n.global.locale.value = internalLocale;
  } else {
    i18n.global.locale = internalLocale;
  }

  setHtmlLocale(locale);
}

function isComposer(
  instance: VueI18n | Composer,
  mode: I18nMode,
): instance is Composer {
  return mode === "composition" && isRef(instance.locale);
}
